/**
 * Auth error message sanitizing (pure — no imports, safe on client and server).
 *
 * `convex/nextjs`'s `fetchAction` throws `new Error(await response.text())`, so
 * when the Convex deployment is reachable but something in front of it (Caddy,
 * Cloudflare) answers with an HTML error page, the *entire page* becomes the
 * error message. `proxyAuthActionToConvex` then forwards that message verbatim
 * as `{ error }`, and the sign-in form renders it — which is how a raw
 * Cloudflare 502 page ends up as literal text inside the form.
 *
 * Everything that could become user-visible auth error text goes through
 * `sanitizeAuthError` first.
 */

/** Shown when the upstream failed rather than rejecting the credentials. */
export const AUTH_UNAVAILABLE_MESSAGE =
  "Sign-in is temporarily unavailable — the authentication service could not be reached. Please try again in a moment.";

/** Shown when there is no usable message at all. */
export const AUTH_GENERIC_MESSAGE = "Something went wrong. Please try again.";

/** Longest message we will render; real auth errors are far shorter. */
export const AUTH_MESSAGE_MAX_LENGTH = 200;

/** An HTML/XML document or fragment rather than a message. */
function looksLikeMarkup(text: string): boolean {
  if (/^\s*</.test(text)) return true;
  return /<!doctype\s|<\s*(html|head|body|title|script|style|div|center|h1|pre)[\s>]|<\/\s*(html|body|div|p|h1|pre)\s*>/i.test(
    text,
  );
}

/** A proxy/gateway failure page or status line, not an auth decision. */
function looksLikeGatewayFailure(text: string): boolean {
  return /(bad gateway|gateway time-?out|service (temporarily )?unavailable|cloudflare|nginx|origin is unreachable|web server is down|connection refused|econnrefused|fetch failed)/i.test(
    text,
  );
}

/**
 * Turn an unknown thrown value or upstream `error` string into something safe
 * to show a signed-out user: never markup, never a whole error page, but real
 * auth messages (bad password, unknown account) still come through.
 */
export function sanitizeAuthError(input: unknown): string {
  let raw: string;
  if (typeof input === "string") {
    raw = input;
  } else if (input instanceof Error) {
    raw = input.message;
  } else if (input === null || input === undefined) {
    raw = "";
  } else if (
    typeof input === "object" &&
    typeof (input as { error?: unknown }).error === "string"
  ) {
    raw = (input as { error: string }).error;
  } else {
    raw = String(input);
  }

  const text = raw.trim();
  if (text === "" || text === "undefined" || text === "null") return AUTH_GENERIC_MESSAGE;
  if (looksLikeMarkup(text) || looksLikeGatewayFailure(text)) return AUTH_UNAVAILABLE_MESSAGE;

  // Collapse whitespace and drop control characters so a multi-line message
  // cannot smuggle layout into the single-line error row.
  const flat = text
    .replace(/[\u0000-\u001F\u007F]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (flat === "") return AUTH_GENERIC_MESSAGE;

  return flat.length > AUTH_MESSAGE_MAX_LENGTH
    ? `${flat.slice(0, AUTH_MESSAGE_MAX_LENGTH - 1).trimEnd()}…`
    : flat;
}
